import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   DoCheck,
   Input,
   OnDestroy,
   OnInit,
   ViewEncapsulation,
} from '@angular/core';
import {
   AbstractControl,
   ControlContainer,
   FormControl,
   FormGroupDirective,
   UntypedFormGroup,
   ValidatorFn,
} from '@angular/forms';
import { ExtensionTypes } from 'generated/graphql-types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { copyNameControlToShortNameAndDisplayName } from 'src/app/common-components/helpers/extension-form-helper';
import { phoneNumberValidator } from 'src/app/common-components/validators/phone-number.validator';
import { ExtensionTypeDetails } from '../../models/extension-type-details';
import { GetExtensionDetail_extension_extensionFmFm } from '../../queries/generated/GetExtensionDetail';

@Component({
   changeDetection: ChangeDetectionStrategy.OnPush,
   encapsulation: ViewEncapsulation.None,
   selector: 'ndt-extension-fmfm-details',
   styleUrls: ['./extension-fmfm-details.component.scss'],
   templateUrl: './extension-fmfm-details.component.html',
   standalone: false,
})
export class ExtensionFmFmDetailsComponent extends ExtensionTypeDetails implements OnInit, OnDestroy, DoCheck {
   @Input() extensionFmFm: GetExtensionDetail_extension_extensionFmFm | null;
   @Input() accountId: number | null;

   form: UntypedFormGroup;
   finalDestinationExtensionTypePhone = false;
   finalDestinationIsExtension = false;

   private destroyed$ = new Subject<void>();

   private destinationTypeControl: AbstractControl | null = null;
   private finalDestinationExtensionControl: AbstractControl | null = null;
   private finalDestinationPhoneNumberControl: AbstractControl | null = null;
   private isSendOriginalCallerIdControl: AbstractControl | null = null;
   private isFinalDestinationVoicemailControl: AbstractControl | null = null;
   private defaultPhoneNumberControl: AbstractControl | null = null;

   constructor(
      private changeDetectorRef: ChangeDetectorRef,
      private controlContainer: ControlContainer,
      private parentForm: FormGroupDirective
   ) {
      super();
   }

   ngOnInit(): void {
      this.initializeForm();
      this.initializeFormValues();
      this.setupSubscriptions();
      copyNameControlToShortNameAndDisplayName(this.parentForm.form, this.form);
   }

   ngOnDestroy(): void {
      this.extensionFmFm = null;
      this.destroyed$.next();
      this.destroyed$.complete();
   }

   private _canEdit: boolean;
   @Input() set canEdit(edit: boolean) {
      if (!edit) {
         this.form?.disable();
      } else {
         this.form?.enable();
      }
      this._canEdit = edit;
   }
   get canEdit(): boolean {
      return this._canEdit;
   }

   private initializeForm(): void {
      this.form = <UntypedFormGroup>this.controlContainer.control;

      this.form.addControl('destinationType', new FormControl({ value: 'extension', disabled: !this.canEdit }));

      this.destinationTypeControl = this.form.get('destinationType');
      this.finalDestinationExtensionControl = this.form.get('finalDestinationExtension');
      this.finalDestinationPhoneNumberControl = this.form.get('finalDestinationPhoneNumber');
      this.isSendOriginalCallerIdControl = this.form.get('isSendOriginalCallerId');
      this.defaultPhoneNumberControl = this.form.get('defaultPhoneNumber');
      this.isFinalDestinationVoicemailControl = this.form.get('isFinalDestinationVoicemail');

      if (this.finalDestinationPhoneNumberControl) {
         this.finalDestinationPhoneNumberControl.setValidators([phoneNumberValidator()]);
      }

      this.form.setValidators(this.finalDestinationValidator());
   }

   private initializeFormValues(): void {
      if (this.extensionFmFm) {
         this.finalDestinationExtensionTypePhone =
            this.extensionFmFm.finalDestinationExtension?.type === ExtensionTypes.Phone;

         if (this.extensionFmFm.finalDestinationExtension?.extensionId != null) {
            this.destinationTypeControl?.setValue('extension');
         } else if (this.extensionFmFm.finalDestinationPhoneNumber) {
            this.destinationTypeControl?.setValue('phoneNumber');
         }
      } else {
         this.destinationTypeControl?.setValue('extension');
         this.finalDestinationExtensionControl?.setValue(null);
      }
   }

   private setupSubscriptions(): void {
      this.subscribeToDestinationType();
      this.subscribeToFinalDestinationExtension();
      this.subscribeToFinalDestinationPhoneNumber();
   }

   private subscribeToDestinationType(): void {
      this.destinationTypeControl?.valueChanges
         .pipe(takeUntil(this.destroyed$))
         .subscribe(this.handleDestinationTypeChange.bind(this));
   }

   private subscribeToFinalDestinationExtension(): void {
      this.finalDestinationExtensionControl?.valueChanges
         .pipe(takeUntil(this.destroyed$))
         .subscribe(this.handleFinalDestinationExtensionChange.bind(this));
   }

   private subscribeToFinalDestinationPhoneNumber(): void {
      this.finalDestinationPhoneNumberControl?.valueChanges
         .pipe(takeUntil(this.destroyed$))
         .subscribe(this.handleFinalDestinationPhoneNumberChange.bind(this));
   }

   private handleDestinationTypeChange(value: string): void {
      this.finalDestinationIsExtension = value === 'extension';
      if (this.finalDestinationIsExtension) {
         this.finalDestinationPhoneNumberControl?.setErrors(null);
      } else {
         this.isFinalDestinationVoicemailControl?.setValue(false);
         this.finalDestinationExtensionControl?.setErrors(null);
      }
      this.form.updateValueAndValidity();
      this.changeDetectorRef.markForCheck();
   }

   private handleFinalDestinationExtensionChange(value: any): void {
      if (value?.extensionId != null) {
         this.finalDestinationPhoneNumberControl?.setValue(null);
         this.markControlsDirty(['finalDestinationPhoneNumber']);
         this.destinationTypeControl?.setValue('extension');
      }

      this.finalDestinationExtensionTypePhone = value?.type === ExtensionTypes.Phone;

      if (!this.finalDestinationExtensionTypePhone) {
         this.isFinalDestinationVoicemailControl?.setValue(false, { emitEvent: false });
         this.isFinalDestinationVoicemailControl?.markAsDirty();
      }

      this.form.updateValueAndValidity();
      this.changeDetectorRef.markForCheck();
   }

   private handleFinalDestinationPhoneNumberChange(value: string): void {
      if (value) {
         this.finalDestinationExtensionControl?.setValue(null);
         this.isFinalDestinationVoicemailControl?.setValue(false);
         this.destinationTypeControl?.setValue('phoneNumber');

         this.markControlsDirty([
            'finalDestinationExtension',
            'isSendOriginalCallerId',
            'defaultPhoneNumber',
            'isFinalDestinationVoicemail',
         ]);
      }

      this.form.updateValueAndValidity();
      this.changeDetectorRef.markForCheck();
   }

   private markControlsDirty(controlNames: string[]): void {
      controlNames.forEach((controlName) => {
         this.form.get(controlName)?.markAsDirty();
      });
   }

   private finalDestinationValidator(): ValidatorFn {
      return (control: AbstractControl): { [key: string]: any } | null => {
         if (
            this.destinationTypeControl?.value === 'extension' &&
            (!this.finalDestinationExtensionControl?.value || !this.finalDestinationExtensionControl.value.extensionId)
         ) {
            return { requiredDestination: true };
         } else if (
            this.destinationTypeControl?.value === 'phoneNumber' &&
            (!this.finalDestinationPhoneNumberControl?.value || this.finalDestinationPhoneNumberControl.value === '')
         ) {
            return { requiredDestination: true };
         }

         return null;
      };
   }

   ngDoCheck(): void {
      if (this.parentForm.form.invalid && (this.parentForm.submitted || this.form.touched)) {
         this.form.markAllAsTouched();
         this.changeDetectorRef.markForCheck();
      }
   }
}
